import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ReportsService } from '../../../../reports.service';
import {
  areColumnsSynonymous,
  buildJoinTypeOptions,
  ColumnMapping,
  DatasetColumn,
  DatasetConfig,
  DatasetJoin,
  DatasourceOption,
  findDatasetColumn,
  findSynonymColumn,
  isDuplicateColumnAlias,
  isDuplicateColumnName,
  isJoinFullyConfigured,
  JoinTypeOption,
  mapApiColumn
} from '../select-datasource-modal.model';

let joinSequence = 0;
let columnMappingSequence = 0;

@Component({
  selector: 'app-dataset-card',
  standalone: false,
  templateUrl: './dataset-card.component.html',
  styleUrl: './dataset-card.component.scss'
})
export class DatasetCardComponent {
  @Input() dataset!: DatasetConfig;
  @Input() index = 0;
  @Input() allDatasets: DatasetConfig[] = [];
  @Input() datasourceOptions: DatasourceOption[] = [];
  @Input() usedTableNames: string[] = [];
  @Input() canRemove = false;

  @Output() remove = new EventEmitter<void>();
  @Input() hasReportAdminRole: boolean = false;

  @ViewChild('calculatedColumnModal') private calculatedColumnModal: any;

  calculatedColumnName = '';
  calculatedColumnExpression = '';
  calculatedColumnNameError: string | null = null;
  calculatedColumnExpressionError: string | null = null;
  calculatedColumnSaving = false;
  calculatedColumnPickerOpen = false;
  calculatedColumnSelectedKeys = new Set<string>();

  constructor(private reportsService: ReportsService, private toastr: ToastrService) {}

  onDatasourceSelected(tableName: string): void {
    this.dataset.table_name = tableName;
    this.dataset.dataset_name = this.datasourceOptions.find(option => option.table_name === tableName)?.dataset_name || '';
    this.dataset.columns = [];
    this.dataset.columnsLoaded = false;
    this.dataset.joins = [];
    this.dataset.relationshipsOpen = false;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
  }

  onToggleManageColumns(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.manageColumnsOpen = !this.dataset.manageColumnsOpen;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
  }

  get allColumnsSelected(): boolean {
    return this.dataset.columns.length > 0 && this.dataset.columns.every(column => column.selected);
  }

  onUseAllColumns(): void {
    const shouldSelectAll = this.dataset.columns.some(column => !column.selected);
    this.dataset.columns.forEach(column => (column.selected = shouldSelectAll));
  }

  get availableJoinTargets(): DatasetConfig[] {
    return this.allDatasets.filter(dataset => dataset.id !== this.dataset.id);
  }

  get relationshipsConfigured(): boolean {
    return this.dataset.joins.length > 0 && this.dataset.joins.every(join => this.isJoinConfigured(join));
  }

  isJoinConfigured(join: DatasetJoin): boolean {
    return isJoinFullyConfigured(join, this.dataset, this.allDatasets);
  }

  // Cached per join so the *ngFor array reference stays stable across change-detection cycles -
  // rebuilding a fresh array on every call here froze the panel (mat-select kept re-rendering its options).
  private joinTypeOptionsCache = new Map<string, { key: string; options: JoinTypeOption[] }>();

  getJoinTypeOptions(join: DatasetJoin): JoinTypeOption[] {
    const target = this.getDatasetById(join.targetDatasetId);
    const targetName = target?.dataset_name || target?.table_name || '';
    const currentName = this.dataset.dataset_name || this.dataset.table_name || '';
    const cacheKey = `${targetName}|${currentName}`;
    const cached = this.joinTypeOptionsCache.get(join.id);
    if (cached && cached.key === cacheKey) {
      return cached.options;
    }
    const options = buildJoinTypeOptions(targetName, currentName);
    this.joinTypeOptionsCache.set(join.id, { key: cacheKey, options });
    return options;
  }

  // Null when the pair is valid or incomplete; a message once both columns are picked but don't refer to the same field.
  getMappingError(join: DatasetJoin, mapping: ColumnMapping): string | null {
    if (!mapping.leftColumn || !mapping.rightColumn) {
      return null;
    }
    const target = this.getDatasetById(join.targetDatasetId);
    const leftColumn = findDatasetColumn(target, mapping.leftColumn);
    const rightColumn = findDatasetColumn(this.dataset, mapping.rightColumn);
    if (leftColumn && rightColumn && !areColumnsSynonymous(leftColumn, rightColumn)) {
      return `"${leftColumn.name}" and "${rightColumn.name}" don't appear to be the same field - map columns that represent the same data across tables.`;
    }
    return null;
  }

  getDatasetById(id: string | null): DatasetConfig | undefined {
    return this.allDatasets.find(dataset => dataset.id === id);
  }

  filterColumns(columns: DatasetColumn[] | undefined, search: string | undefined): DatasetColumn[] {
    const list = columns || [];
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter(column => column.name.toLowerCase().includes(term));
  }

  onToggleRelationships(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.relationshipsOpen = !this.dataset.relationshipsOpen;
    if (this.dataset.relationshipsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
      if (!this.dataset.joins.length) {
        this.onAddJoin();
      }
    }
  }

  trackByJoinId(_: number, join: DatasetJoin): string {
    return join.id;
  }

  trackByMappingId(_: number, mapping: ColumnMapping): string {
    return mapping.id;
  }

  onAddJoin(): void {
    const join = this.createJoin();
    this.dataset.joins.push(join);
    const target = this.getDatasetById(join.targetDatasetId);
    if (target) {
      this.ensureDatasetColumnsLoaded(target);
    }
  }

  onRemoveJoin(join: DatasetJoin): void {
    this.dataset.joins = this.dataset.joins.filter(item => item.id !== join.id);
    this.joinTypeOptionsCache.delete(join.id);
  }

  onJoinTargetChange(join: DatasetJoin): void {
    join.columnMappings = [this.createColumnMapping()];
    const target = this.getDatasetById(join.targetDatasetId);
    if (target) {
      this.ensureDatasetColumnsLoaded(target);
    }
  }

  onAddColumnMapping(join: DatasetJoin): void {
    join.columnMappings.push(this.createColumnMapping());
  }

  onRemoveColumnMapping(join: DatasetJoin, mapping: ColumnMapping): void {
    join.columnMappings = join.columnMappings.filter(item => item.id !== mapping.id);
  }

  // Auto-fills the OTHER side only while it's still empty, so a later change never bounces back
  // and overrides a column the user (or the earlier auto-select) already put in place.
  onLeftColumnChange(join: DatasetJoin, mapping: ColumnMapping): void {
    if (mapping.rightColumn) {
      return;
    }
    const leftColumn = findDatasetColumn(this.getDatasetById(join.targetDatasetId), mapping.leftColumn);
    const match = leftColumn ? findSynonymColumn(leftColumn, this.dataset.columns) : undefined;
    if (match) {
      mapping.rightColumn = match.key;
    }
  }

  onRightColumnChange(join: DatasetJoin, mapping: ColumnMapping): void {
    if (mapping.leftColumn) {
      return;
    }
    const targetDataset = this.getDatasetById(join.targetDatasetId);
    const rightColumn = findDatasetColumn(this.dataset, mapping.rightColumn);
    const match = rightColumn && targetDataset ? findSynonymColumn(rightColumn, targetDataset.columns) : undefined;
    if (match) {
      mapping.leftColumn = match.key;
    }
  }

  private createJoin(): DatasetJoin {
    joinSequence += 1;
    return {
      id: `join-${joinSequence}`,
      targetDatasetId: this.availableJoinTargets[0]?.id ?? null,
      joinType: null,
      columnMappings: [this.createColumnMapping()]
    };
  }

  private createColumnMapping(): ColumnMapping {
    columnMappingSequence += 1;
    return { id: `mapping-${columnMappingSequence}`, leftColumn: null, rightColumn: null };
  }

  get canAddCalculatedColumn(): boolean {
    return this.dataset.columnsLoaded && !this.dataset.columnsLoading &&
       !!this.calculatedColumnName.trim() && !!this.calculatedColumnExpression.trim() &&
       !isDuplicateColumnName(this.dataset.columns, this.calculatedColumnName) &&
       !isDuplicateColumnAlias(this.dataset.columns, this.calculatedColumnExpression) &&
       !this.calculatedColumnSaving;  
  }

  onOpenAddCalculatedColumn(): void {
    this.calculatedColumnName = '';
    this.calculatedColumnExpression = '';
    this.calculatedColumnNameError = null;
    this.calculatedColumnExpressionError = null;
    this.calculatedColumnSaving = false;
    this.calculatedColumnPickerOpen = false;
    this.calculatedColumnSelectedKeys = new Set<string>();
    this.ensureDatasetColumnsLoaded(this.dataset);
    this.calculatedColumnModal?.show();
  }

  onCloseAddCalculatedColumn(): void {
    this.calculatedColumnModal?.hide();
  }

  onCalculatedColumnNameChange(): void {
    this.calculatedColumnNameError = isDuplicateColumnName(this.dataset.columns, this.calculatedColumnName)
      ? 'Column name is already available.'
      : null;
  }

  onCalculatedColumnExpressionChange(): void {
    this.calculatedColumnExpressionError = isDuplicateColumnAlias(this.dataset.columns, this.calculatedColumnExpression)
      ? 'Duplicate name or equivalent calculation'
      : null;
  }

  onToggleColumnPicker(): void {
    this.calculatedColumnPickerOpen = !this.calculatedColumnPickerOpen;
  }

  isColumnPicked(column: DatasetColumn): boolean {
    return this.calculatedColumnSelectedKeys.has(column.key);
  }

  onToggleColumnPick(column: DatasetColumn): void {
    if (this.calculatedColumnSelectedKeys.has(column.key)) {
      this.calculatedColumnSelectedKeys.delete(column.key);
    } else {
      this.calculatedColumnSelectedKeys.add(column.key);
    }
  }

  // Appends the picked column names (hyphen-joined when more than one) to the expression so the user can keep editing.
  onInsertSelectedColumns(): void {
    const names = this.dataset.columns
      .filter(column => this.calculatedColumnSelectedKeys.has(column.key))
      .map(column => column.name);
    if (!names.length) {
      this.calculatedColumnPickerOpen = false;
      return;
    }
    const insertion = names.join(' ');
    const current = this.calculatedColumnExpression;
    this.calculatedColumnExpression = current && !current.endsWith(' ') ? `${current} ${insertion}` : `${current}${insertion}`;
    this.onCalculatedColumnExpressionChange();
    // Keep the picked keys so reopening the picker still shows them checked - don't clear the Set here.
    this.calculatedColumnPickerOpen = false;
  }

  onAddCalculatedColumn(): void {
    if (!this.canAddCalculatedColumn) {
      return;
    }
    this.calculatedColumnSaving = true;
    const payload = {
      table_name: this.dataset.table_name,
      dataset_name: this.dataset.dataset_name,
      column_name: this.calculatedColumnName.trim(),
      alias: this.calculatedColumnExpression.trim(),
      table_description: this.getColumnDescription(),
      data_type: 'NVARCHAR(255)',
      DataType_Category: 'Text'
    };
    this.reportsService.addCalculatedColumn(payload).subscribe({
      next: (response: any) => {
        this.calculatedColumnSaving = false;
        const newColumn = mapApiColumn(response?.column ?? {
           ...payload,
           column_description: payload.table_description
         });
        this.dataset.columns = [...this.dataset.columns, newColumn];
        this.toastr.success('Calculated column added successfully', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
        this.onCloseAddCalculatedColumn();
      },
      error: () => {
        this.calculatedColumnSaving = false;
        this.toastr.error('Failed to add calculated column', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
      }
    });
  }

  getColumnDescription() :string {
    let description = 'Combination of the Unique ID of the ';
    if (!this.calculatedColumnSelectedKeys.size) {
      return '';
    }
    [...this.calculatedColumnSelectedKeys].forEach((key,index) => {
      if(index > 0) {
        if(index < this.calculatedColumnSelectedKeys.size - 1) {
          description += ' and ';
        }else{
          description += ' , ';
        }
      }
      description += key;
    });
    return description;
  }

  private ensureDatasetColumnsLoaded(target: DatasetConfig): void {
    if (!target.table_name || target.columnsLoaded || target.columnsLoading) {
      return;
    }
    target.columnsLoading = true;
    this.reportsService.getDatasourceColumns({ type: 'columnInfo', tableName: target.table_name, datasetName: target.dataset_name }).subscribe({
      next: (columns: any[]) => {
        target.columns = (columns || []).map(column => mapApiColumn(column));
        target.columnsLoaded = true;
        target.columnsLoading = false;
      },
      error: () => {
        target.columnsLoading = false;
      }
    });
  }
}
